import { useMemo, useState } from "react";
import { Check, Search, SlidersHorizontal, Ruler, Minus, Plus } from "lucide-react";
import { money, effectiveOptions, unitPrice, slug, splitMulti, multiQty } from "@/lib/api";
import { fitsSpec, hullOf, hullLabel } from "@/lib/fit";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export const BoatConfigurator = ({ product, selection, optionSets, onVariant, onVariantQty, onAddon }) => {
  const { variants: allVariants, addons: allAddons } = effectiveOptions(product, optionSets);
  const [query, setQuery] = useState("");
  const [showAll, setShowAll] = useState(false);
  const picked = useMemo(() => new Set(selection.addons || []), [selection.addons]);
  const price = unitPrice(product, selection.variants, selection.addons, optionSets);
  const hull = hullOf(product);
  const width = showAll ? null : hull?.width;

  const isChosen = (g, o) => (g.multi ? splitMulti(selection.variants[g.name]).includes(o.label) : selection.variants[g.name] === o.label);
  const chosenUpgrade = (g) => g.options.some((o) => isChosen(g, o) && o.label !== "None");
  const variants = allVariants
    .filter((g) => fitsSpec(g.fit_widths, width) || chosenUpgrade(g))
    .map((g) => ({ ...g, options: g.options.filter((o) => fitsSpec(o.fit_widths, width) || isChosen(g, o)) }))
    .filter((g) => g.options.length > 1 || chosenUpgrade(g));
  const addons = allAddons.filter((a) => fitsSpec(a.fit_widths, width) || picked.has(a.label));
  const hiddenCount =
    allVariants.reduce((n, g) => n + g.options.length, 0) + allAddons.length - variants.reduce((n, g) => n + g.options.length, 0) - addons.length;

  const visibleAddons = addons.filter((a) => !query || a.label.toLowerCase().includes(query.toLowerCase()));
  const upgradesChosen = variants.filter((g) => selection.variants[g.name] && selection.variants[g.name] !== "None").length;
  if (allVariants.length === 0 && allAddons.length === 0) return null;

  return (
    <div data-testid="boat-configurator" className="mt-8 rounded-lg border border-border bg-card">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border p-5">
        <div className="flex items-center gap-2.5">
          <SlidersHorizontal size={18} className="text-[#1B66C9]" />
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#1B66C9]">Configure</p>
            <h3 className="font-display text-lg font-black leading-tight">{product.name}</h3>
          </div>
        </div>
        <div className="text-right">
          <span className="block text-[10px] font-bold uppercase tracking-wider text-muted-foreground">As configured</span>
          <span data-testid="configurator-price" className="font-display text-2xl font-black text-[#1B66C9]">
            {money(price)}
          </span>
        </div>
      </div>

      {hull?.width != null && (hiddenCount > 0 || showAll) && (
        <div
          data-testid="fit-banner"
          className="flex flex-wrap items-center justify-between gap-2 border-b border-border bg-secondary/50 px-5 py-2.5 text-xs"
        >
          <span className="flex items-center gap-1.5 font-semibold">
            <Ruler size={13} className="text-[#1B66C9]" />
            {showAll
              ? `Showing every option, including ones sized for other hulls`
              : `Showing options sized for your ${hullLabel(hull)} hull · ${hiddenCount} hidden`}
          </span>
          <button
            data-testid="fit-toggle"
            onClick={() => setShowAll((v) => !v)}
            className="font-bold uppercase tracking-wider text-[#1B66C9] hover:underline"
          >
            {showAll ? "Show only what fits" : "Show all"}
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 gap-8 p-5 lg:grid-cols-2">
        {variants.length > 0 && (
          <section>
            <div className="flex items-baseline justify-between">
              <h4 className="text-xs font-bold uppercase tracking-[0.2em]">Upgrades</h4>
              <span className="text-[11px] text-muted-foreground">{upgradesChosen} chosen</span>
            </div>
            <div className="mt-3 space-y-3">
              {variants.map((g) => {
                const value = selection.variants[g.name] || g.options[0].label;
                const chosen = value !== "None" && value !== g.options[0].label;
                if (g.multi) {
                  const picked = new Set(splitMulti(value));
                  return (
                    <div key={g.name} data-testid={`upgrade-${slug(g.name)}`} className="rounded-md border border-border p-2.5">
                      <div className="flex items-baseline justify-between">
                        <span className={`text-sm ${picked.size ? "font-bold" : "text-muted-foreground"}`}>{g.name}</span>
                        <span className="text-[10px] font-bold uppercase tracking-wider text-[#1B66C9]">Pick any</span>
                      </div>
                      <ul className="mt-1.5 space-y-0.5">
                        {g.options
                          .filter((o) => o.label !== "None")
                          .map((o) => {
                            const on = picked.has(o.label);
                            const qty = o.per_unit ? multiQty(value, o.label) : 0;
                            const tid = `upgrade-${slug(g.name)}-${slug(o.label)}`;
                            return (
                              <li key={o.label}>
                                <button
                                  type="button"
                                  data-testid={tid}
                                  aria-pressed={on}
                                  onClick={() => onVariant(product, g.name, o.label)}
                                  className={`flex w-full items-center gap-2.5 rounded-md border px-2.5 py-1.5 text-left text-xs transition-colors ${
                                    on ? "border-[#1B66C9] bg-[#1B66C9]/5" : "border-transparent hover:bg-secondary"
                                  }`}
                                >
                                  <span
                                    className={`flex flex-shrink-0 items-center justify-center rounded border ${on ? "border-[#1B66C9] bg-[#1B66C9] text-white" : "border-border"}`}
                                    style={{ width: 16, height: 16 }}
                                  >
                                    {on && <Check size={11} strokeWidth={3} />}
                                  </span>
                                  <span className={`flex-1 ${on ? "font-semibold" : ""}`}>{o.label}</span>
                                  {o.per_unit && on && (
                                    <span className="flex items-center gap-1" onClick={(e) => e.stopPropagation()} data-testid={`${tid}-qty`}>
                                      <span
                                        role="button"
                                        aria-label="Fewer"
                                        data-testid={`${tid}-minus`}
                                        onClick={() => onVariantQty(product, g.name, o.label, qty - 1)}
                                        className="flex h-5 w-5 items-center justify-center rounded border border-border bg-background hover:bg-secondary"
                                      >
                                        <Minus size={10} />
                                      </span>
                                      <span className="w-5 text-center font-bold" data-testid={`${tid}-count`}>{qty}</span>
                                      <span
                                        role="button"
                                        aria-label="More"
                                        data-testid={`${tid}-plus`}
                                        onClick={() => onVariantQty(product, g.name, o.label, qty + 1)}
                                        className="flex h-5 w-5 items-center justify-center rounded border border-border bg-background hover:bg-secondary"
                                      >
                                        <Plus size={10} />
                                      </span>
                                    </span>
                                  )}
                                  {Number(o.price_delta) !== 0 && (
                                    <span className="font-bold text-[#1B66C9]">
                                      {o.per_unit && on && qty > 1 ? `+${money(o.price_delta * qty)}` : `+${money(o.price_delta)}${o.per_unit ? " ea" : ""}`}
                                    </span>
                                  )}
                                </button>
                              </li>
                            );
                          })}
                      </ul>
                    </div>
                  );
                }
                return (
                  <div key={g.name} className="grid grid-cols-5 items-center gap-2">
                    <label className={`col-span-2 truncate text-sm ${chosen ? "font-bold" : "text-muted-foreground"}`} title={g.name}>
                      {g.name}
                    </label>
                    <div className="col-span-3">
                      <Select value={value} onValueChange={(v) => onVariant(product, g.name, v)}>
                        <SelectTrigger data-testid={`upgrade-${slug(g.name)}`} className={`h-9 text-xs ${chosen ? "border-[#1B66C9]" : ""}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="max-h-72">
                          {g.options.map((o) => (
                            <SelectItem key={o.label} value={o.label} data-testid={`upgrade-${slug(g.name)}-${slug(o.label)}`} className="text-xs">
                              {o.label}
                              {Number(o.price_delta) > 0 && (
                                <span className="ml-1.5 font-bold text-[#1B66C9]">+{money(o.price_delta)}</span>
                              )}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {addons.length > 0 && (
          <section>
            <div className="flex items-center justify-between gap-3">
              <h4 className="text-xs font-bold uppercase tracking-[0.2em]">
                Add-ons <span className="ml-1 text-muted-foreground">{picked.size} selected</span>
              </h4>
              {addons.length > 8 && (
                <div className="relative">
                  <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input
                    data-testid="addon-search-input"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search add-ons"
                    className="h-8 w-44 rounded-md border border-border bg-background pl-7 pr-2 text-xs"
                  />
                </div>
              )}
            </div>
            <ul className="scroll-blue mt-3 max-h-[420px] space-y-1 overflow-y-auto pr-2" data-testid="addon-list">
              {visibleAddons.map((a) => {
                const on = picked.has(a.label);
                return (
                  <li key={a.label}>
                    <button
                      data-testid={`addon-${slug(a.label)}`}
                      onClick={() => onAddon(product, a.label)}
                      className={`flex w-full items-center gap-3 rounded-md border px-3 py-2 text-left text-sm transition-colors ${
                        on ? "border-[#1B66C9] bg-[#1B66C9]/5" : "border-transparent hover:bg-secondary"
                      }`}
                    >
                      <span
                        className={`flex h-4.5 w-4.5 flex-shrink-0 items-center justify-center rounded border ${
                          on ? "border-[#1B66C9] bg-[#1B66C9] text-white" : "border-border"
                        }`}
                        style={{ width: 18, height: 18 }}
                      >
                        {on && <Check size={12} strokeWidth={3} />}
                      </span>
                      <span className={`flex-1 capitalize ${on ? "font-semibold" : ""}`}>{a.label}</span>
                      <span className={`text-xs font-bold ${Number(a.price) < 0 ? "text-[#16A34A]" : "text-muted-foreground"}`}>
                        {Number(a.price) < 0 ? `−${money(-a.price)}` : `+${money(a.price)}`}
                      </span>
                    </button>
                  </li>
                );
              })}
              {visibleAddons.length === 0 && (
                <li className="py-6 text-center text-xs text-muted-foreground">No add-ons match.</li>
              )}
            </ul>
            {visibleAddons.length > 8 && (
              <p className="mt-1.5 text-center text-[11px] font-bold text-[#1B66C9]" data-testid="addon-scroll-hint">
                ↕ Scroll the list — {visibleAddons.length} add-ons available
              </p>
            )}
          </section>
        )}
      </div>
    </div>
  );
};
